import { useMemo } from 'react';

import { MenuItemChild } from '@da-g3-acl/shared/common';

import { ImageTypeMenuItem } from '../types/imageTypeMenuItem';
import { RowMenuItem } from '../types/rowMenuItem';

export const serialiseImageTypeId = (id: number): string => `imageType:${id}`;
export const deserialiseImageTypeId = (idSerialised: string): number =>
	parseInt(idSerialised.replace('imageType:', ''));

/**
 * Based on a flat list of image type menu item intermediate objects, build a tree of status bar menu items, grouping them according to each item key, and mapping appropriate common properties
 * @param {ImageTypeMenuItem[] | undefined} imageTypes
 * @returns {MenuItemChild[]}
 */
export const useMappedImageTypeMenuItems = (imageTypes: ImageTypeMenuItem[] | undefined): MenuItemChild[] => {
	return useMemo(() => {
		const groupedImageTypeMenuItems =
			imageTypes?.reduce(
				(agg, next) => ({
					...agg,
					[next.groupName]: [...(agg[next.groupName] ?? []), next],
				}),
				{} as Record<string, ImageTypeMenuItem[]>,
			) ?? {};

		const groupCount = Object.keys(groupedImageTypeMenuItems).length;

		return Object.keys(groupedImageTypeMenuItems).reduce((agg, groupName) => {
			const childCount = groupedImageTypeMenuItems[groupName].length;
			if (childCount >= 2 && groupCount > 1)
				return [
					...agg,
					{
						id: groupName,
						name: groupName,
						sectionName: 'Imagery',
						children: groupedImageTypeMenuItems[groupName].map<MenuItemChild>((i) => ({
							id: serialiseImageTypeId(i.id),
							name: i.name,
							tag: i.tag,
							sectionName: `Imagery > ${i.groupName}`,
							children: [],
						})),
					} as MenuItemChild,
				];
			else
				return [
					...agg,
					...groupedImageTypeMenuItems[groupName].map<MenuItemChild>((i) => ({
						id: serialiseImageTypeId(i.id),
						name: i.name,
						tag: i.tag,
						sectionName: 'Imagery > ' + i.groupName,
						children: [],
					})),
				];
		}, [] as MenuItemChild[]);
	}, [imageTypes]);
};

/**
 * Based on a flat list of row data menu item intermediate objects, build a tree of status bar menu items, grouping them according to each item key, and mapping appropriate common properties
 * @param {ImageTypeMenuItem[] | undefined} rowMenuItems
 * @returns {MenuItemChild[]}
 */
export const useMappedRowMenuItems = (rowMenuItems: RowMenuItem[] | undefined): MenuItemChild[] | undefined => {
	return useMemo(() => {
		if (!rowMenuItems) return undefined;

		const groupedBySectionThenGroup: Record<string, Record<string, RowMenuItem[]>> = {};

		for (const rowMenuItem of rowMenuItems) {
			if (groupedBySectionThenGroup[rowMenuItem.sectionName] === undefined)
				groupedBySectionThenGroup[rowMenuItem.sectionName] = {};

			if (groupedBySectionThenGroup[rowMenuItem.sectionName][rowMenuItem.groupName] === undefined)
				groupedBySectionThenGroup[rowMenuItem.sectionName][rowMenuItem.groupName] = [];

			groupedBySectionThenGroup[rowMenuItem.sectionName][rowMenuItem.groupName] = [
				...((groupedBySectionThenGroup[rowMenuItem.sectionName] ?? {})?.[rowMenuItem.groupName] ?? []),
				rowMenuItem,
			];
		}

		const mappedRowMenuItems: MenuItemChild[] = [];

		const formatSectionName = (item: RowMenuItem): string =>
			item.groupName ? `${item.sectionName} > ${item.groupName}` : item.sectionName;

		for (const sectionName of Object.keys(groupedBySectionThenGroup)) {
			for (const groupName of Object.keys(groupedBySectionThenGroup[sectionName])) {
				const sectionGroupItems = groupedBySectionThenGroup[sectionName][groupName];

				if (sectionGroupItems.length >= 2 && groupName !== '') {
					mappedRowMenuItems.push({
						id: groupName,
						name: groupName,
						sectionName: sectionName,
						tag: sectionGroupItems[0].marker,
						onDownload: sectionGroupItems[0].onDownload,
						children: sectionGroupItems.map<MenuItemChild>((i) => ({
							id: i.id,
							name: i.name,
							sectionName: formatSectionName(i),
							tag: i.marker,
							children: [],
						})),
					} satisfies MenuItemChild);
				} else {
					for (const sectionGroupItem of sectionGroupItems) {
						mappedRowMenuItems.push({
							id: sectionGroupItem.id,
							name: sectionGroupItem.name,
							sectionName: formatSectionName(sectionGroupItem),
							children: [],
							tag: sectionGroupItem.marker,
							onDownload: sectionGroupItem.onDownload,
						} satisfies MenuItemChild);
					}
				}
			}
		}

		return mappedRowMenuItems;
	}, [rowMenuItems]);
};
